// Enhanced PDF Generation Utility for Quotation
// Created on 2026-02-07 by Farhan Khan - Branded, Colorful, Professional PDF
// Structure: Page 1 (Summary), Page 2 (Mounting Structure), Page 3 (BOM)
// NOTE: Requires jsPDF and jspdf-autotable packages
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const safe = (val) =>
  val === undefined || val === null || val === ""
    ? ""
    : val;

const toStringSafe = (val) =>
  typeof val === "object"
    ? JSON.stringify(val)
    : String(val || "");

// Company details constants
const COMPANY_NAME = "Solis Power Greentech Pvt Ltd";
const COMPANY_ADDRESS_LINE1 = "SB-10, Narayan Vihar, Ajmer Road Jaipur";
const COMPANY_ADDRESS_LINE2 = "Rajasthan - 302035";
const COMPANY_GSTIN = "GSTIN : 08ACYFS1632G1ZQ";

// Bank Details Constants
const BANK_NAME = "ICICI BANK LTD";
const ACCOUNT_NAME = "SOLISPOWER GREENTECH PVT LTD";
const ACCOUNT_NO = "361305500447";
const IFSC = "ICIC0003613";
const BRANCH = "BHANKROTA, JAIPUR";

// Brand Colors - Created on 2026-02-07 by Farhan Khan
const BRAND_PRIMARY = [54, 71, 148]; // #364794 - Dark blue
const BRAND_SECONDARY = [102, 126, 234]; // #667EEA - Light blue
const BRAND_ACCENT = [255, 193, 7]; // #FFC107 - Gold/Amber
const BRAND_SUCCESS = [40, 167, 69]; // #28A745 - Green
const BRAND_LIGHT_BG = [245, 247, 250]; // #F5F7FA - Light gray background
const BRAND_TEXT = [33, 37, 41]; // #212529 - Dark text

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (error) {
    console.warn('Date formatting error:', error);
    return "";
  }
};

// Convert HTML to array of plain text lines (preserves bullets, line breaks)
const htmlToLines = (html) => {
  if (!html || typeof html !== 'string') return [];
  let text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  return text.split('\n').map(s => s.trim()).filter(s => s.length > 0);
};

// Format currency helper - Prefix with Rs
const formatCurrency = (value) => {
  try {
    return `Rs. ${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  } catch (error) {
    console.warn('Currency formatting error:', error);
    return 'Rs. 0.00';
  }
};

// Load logo as base64 (async)
const loadLogoAsBase64 = async (logoUrl = '/solisPowerLogo.png') => {
  try {
    const response = await fetch(logoUrl);
    if (!response.ok) {
      throw new Error('Logo not found');
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Logo loading failed, PDF will continue without logo:', error);
    return null;
  }
};

// Load cover image as base64 (async)
const loadCoverImageAsBase64 = async () => {
  try {
    const imageUrl = 'https://i.ibb.co/wZVWdqqy/Final-Quatation-image-new.webp';
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error('Cover image not found');
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Cover image loading failed:', error);
    return null;
  }
};
// Load QR code as base64 (async)
const loadQRCodeAsBase64 = async () => {
  try {
    const imageUrl = 'https://i.ibb.co/7dXwQfRy/sssssdsds.png'; // QR Code Image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error('QR code image not found');
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('QR code loading failed:', error);
    return null;
  }
};

// Load Social Media Icons as base64 (async)
const loadSocialIcons = async () => {
  const iconUrls = {
    linkedin: "https://i.ibb.co/yFPPdQdY/linkdn.png",
    youtube: "https://i.ibb.co/35FVdnK5/Youtube-logo.png",
    instagram: "https://i.ibb.co/93nn9Gj3/Insta-logo.png",
    facebook: "https://i.ibb.co/5xkMpWbr/Facebook-logo-square.png"
  };

  const keys = Object.keys(iconUrls);
  const results = await Promise.all(keys.map(async (key) => {
    try {
      const response = await fetch(iconUrls[key]);
      if (!response.ok) throw new Error(`${key} icon not found`);
      const blob = await response.blob();
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      return { key, base64 };
    } catch (error) {
      console.warn(`${key} icon loading failed:`, error);
      return { key, base64: null };
    }
  }));

  const icons = {};
  results.forEach(res => {
    icons[res.key] = res.base64;
  });
  return icons;
};

// Generate PDF function - Enhanced Professional Structure
// Created on 2026-02-07 by Farhan Khan
// Structure: Page 1 (Summary), Page 2 (Mounting Structure), Page 3 (BOM)
export const generateQuotationPDF = async (quotationData) => {
  if (!quotationData) {
    throw new Error('Quotation data is required');
  }

  // STEP 2: DEBUG (MANDATORY)
  console.log("MOUNTING DATA IN PDF", quotationData.mountingStructure || quotationData.mountingStructures);

  const { quotation, lineItems, bomItems, leadData, mountingStructure, mountingStructures, mountingStructureItems } = quotationData;

  if (!quotation || !quotation.id) {
    console.error('PDF Generation - Invalid quotation:', quotation);
    throw new Error('Invalid quotation data: quotation object or ID is missing');
  }

  // 📝 DATA BINDING & NORMALIZATION (Robust Array Fallback Helper)
  const fallbackArray = (...arrays) => {
    for (const arr of arrays) {
      if (Array.isArray(arr) && arr.length > 0) {
        return arr;
      }
    }
    // Fallback to first array if all are empty
    for (const arr of arrays) {
      if (Array.isArray(arr)) {
        return arr;
      }
    }
    return [];
  };

  const safeLineItems = fallbackArray(
    lineItems,
    quotation?.lineItems,
    quotation?.items
  );

  const safeBomItems = fallbackArray(
    bomItems,
    quotation?.bomItems,
    quotation?.quotation_bom_items,
    quotation?.bom_items
  );

  // Normalize Mounting Structure
  let safeMountingStructures = [];
  const rawMounting = [
    mountingStructure,
    mountingStructures,
    mountingStructureItems,
    quotationData.mounting_structures,
    quotation?.mounting_structures,
    quotation?.mounting_structure
  ];
  for (const item of rawMounting) {
    if (Array.isArray(item) && item.length > 0) {
      safeMountingStructures = item;
      break;
    } else if (item && item.sections && Array.isArray(item.sections) && item.sections.length > 0) {
      safeMountingStructures = item.sections;
      break;
    }
  }
  if (safeMountingStructures.length === 0) {
    for (const item of rawMounting) {
      if (Array.isArray(item)) {
        safeMountingStructures = item;
        break;
      } else if (item && Array.isArray(item.sections)) {
        safeMountingStructures = item.sections;
        break;
      }
    }
  }

  console.log("FINAL MOUNTING STRUCTURE:", safeMountingStructures);

  // Normalize Created By name
  const createdByName = quotation.created_by_user?.name ||
    quotation.CREATEDBYNAME ||
    quotation.createdbyname ||
    quotation.created_by_name ||
    '';

  // Extract Bill To information from Lead or fallback to quotation fields
  const billToName = quotation?.lead?.name || quotation?.lead?.company_name ||
    (leadData?.firstname && leadData?.lastname
      ? `${leadData.firstname} ${leadData.lastname}`.trim()
      : (leadData?.name || quotation.customer_name || ''));
  console.log("Quatation2222", quotation, "Lead", quotationData.leadData);
  const billToAddress = safe(quotationData?.leadData?.customer_address || '');
  const billToCity = safe(quotationData?.leadData?.customer_city || quotationData.leadData?.city || quotation.customer_city || '');
  const billToState = safe(quotationData?.leadData?.customer_state || quotationData.leadData?.state || quotation.customer_state || '');
  const billToCountry = safe(quotationData?.leadData?.country || quotationData.leadData?.country || 'India');
  const billToPhone = safe(quotationData?.leadData?.phone || quotationData.leadData?.mobile || leadData?.phone || quotation.customer_mobile || '');
  const billToEmail = safe(quotationData?.leadData?.email || quotationData.leadData?.email || '');
  const safePhone = (billToPhone && billToPhone !== "null" && billToPhone !== "N/A") ? billToPhone : "";
  const safeEmail = (billToEmail && billToEmail !== "null" && billToEmail !== "N/A") ? billToEmail : "";
  const billToGst = safe(quotation?.leadData?.gst_no || leadData?.gst_no || '');
  const billToProject = safe(quotation?.leadData?.project_line_items?.site_name || quotation?.customer_name || '');
  const billToProjectType = safe(quotation?.leadData?.project_line_items?.type_of_project || '');
  const quotationNo = safe(quotation.estimate_number || "Draft");
  const quotationNumber = quotationNo;
  const leadName = billToName;
  const createdBy = createdByName;

  // Extract Creator Details Safely
  const createdByUserDetails = quotation?.created_by_user ||
    quotation?.created_by_details ||
    quotation?.createdByUser ||
    quotation?.user ||
    {};

  const creatorName = createdByUserDetails.name || createdBy || "N/A";
  const creatorMobile = createdByUserDetails.mobile || createdByUserDetails.phone || "N/A";
  const creatorEmail = createdByUserDetails.email || "N/A";

  const totalPagesFinal = 3; // Estimated base, dynamically updated by some flows or just used as placeholder

  console.log('PDF Generation - Starting:', {
    quotationId: quotation.id,
    estimateNumber: quotationNo,
    hasLeadData: !!quotation?.lead || !!leadData,
    billToName: billToName,
    billToPhone: billToPhone,
    createdByName: createdByName,
    hasLineItems: safeLineItems.length > 0,
    hasBomItems: safeBomItems.length > 0,
    hasMountingStructures: safeMountingStructures.length > 0,
    mountingStructuresCount: safeMountingStructures.length,
    lineItemsCount: safeLineItems.length,
    bomItemsCount: safeBomItems.length
  });

  try {
    const logoBase64 = await loadLogoAsBase64('/solisPowerLogo.png');
    const logoTransparentBase64 = await loadLogoAsBase64('/solislogoTrasparent.png');
    const coverImageBase64 = await loadCoverImageAsBase64();
    const qrCodeBase64 = await loadQRCodeAsBase64();
    const socialIcons = await loadSocialIcons();

    // Create new PDF document (A4 size)
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    const borderMargin = 5;
    const footerHeight = 12; // Standard compact height
    const headerHeightConst = footerHeight; // STRICT: Match footer height exactly
    let yPosition = margin + headerHeightConst; // Start below header area
    let totalPages = 1;
    const centerX = pageWidth / 2;

    // Page 3+ Layout Constants (Unified Grid System)
    const leftMarginVal = 14;
    const logoAreaWidth = 40; // Logo at pageWidth - 40
    const safeGap = 2; // Gap between content and logo area
    const contentEndX = pageWidth - logoAreaWidth - safeGap; // 168mm for A4
    const contentWidth = contentEndX - leftMarginVal; // 154mm usable width

    // Helper function to draw page border (Page 2+ Vertical Bars Only)
    const drawPageBorder = (pageNum) => {
      // 🚫 Page 1 → NO BORDER
      if (pageNum === 1) return;

      doc.setDrawColor(...BRAND_PRIMARY);
      doc.setLineWidth(0.8);

      const sideMargin = borderMargin;
      const topBarY = 0; // Starts from top
      const bottomBarY = pageHeight; // Goes to bottom

      // LEFT SIDE VERTICAL BAR
      doc.line(sideMargin, topBarY, sideMargin, bottomBarY);

      // RIGHT SIDE VERTICAL BAR
      doc.line(pageWidth - sideMargin, topBarY, pageWidth - sideMargin, bottomBarY);
    };

    // Helper function to add quotation number header at top-left
    // Created on 2026-02-07 by Farhan Khan - Quotation number on all pages
    const addQuotationNumberHeader = (quotationNumber) => {
      // const headerX = margin;
      // const headerY = margin + 5; // Top-left position

      // doc.setFontSize(10);
      // doc.setFont('helvetica', 'bold');
      // doc.setTextColor(51, 51, 51); // #333 color
      // const headerText = `Quotation No: ${quotationNumber || 'Draft'}`;
      // doc.text(headerText, headerX, headerY);

      // // Reset text color for normal content
      // doc.setTextColor(...BRAND_TEXT);
    };

    // Helper function to add Compact Premium Header (Logo Left + QNo Right)
    const addHeader = (pageNum, quotationNumber) => {
      // ❌ Skip header on first page
      if (pageNum === 1) return;

      // Prevent duplicate rendering on same page
      const headerFlag = `_header_rendered_${pageNum}`;
      if (doc[headerFlag]) return;
      doc[headerFlag] = true;

      const headerBarHeight = footerHeight; // Standard 12mm
      const textY = (headerBarHeight / 2) + 1.5;

      // DRAW HEADER BACKGROUND
      doc.setFillColor(11, 31, 58); // #0B1F3A
      doc.rect(0, 0, pageWidth, headerBarHeight, 'F');

      // TEXT COLOR WHITE
      doc.setTextColor(255, 255, 255);

      // Company Logo (Right on Page 2, beside text on Page 3+)
      const currentLogo = pageNum >= 3 ? (logoTransparentBase64 || logoBase64) : logoBase64;
      if (currentLogo) {
        try {
          const imgProps = doc.getImageProperties(currentLogo);
          const aspectRatio = imgProps.height / imgProps.width;
          const logoW = 22; // Made logo a little smaller for better interactive look
          const logoH = logoW * aspectRatio;
          let logoX = pageWidth - 40;
          let logoY = 15;

          if (pageNum >= 3) {
            logoX = (pageWidth - logoW) / 2;
            logoY = (headerBarHeight - logoH) / 2;
          }

          doc.addImage(currentLogo, 'PNG', logoX, logoY, logoW, logoH);
        } catch (logoError) {
          console.warn('Header logo failed:', logoError);
        }
      }

      // LEFT → Proposal Title (Compact 8.5pt)
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.text("Proposal for Solar Power Plant", margin, textY, { align: "left" });

      // RIGHT → Quotation Number (8.5pt)
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      const qNum = quotationNumber || quotation?.quotation_no || quotation?.estimate_number || "Draft";
      doc.text(`Quotation No: ${qNum}`, pageWidth - margin, textY, { align: "right" });

      // RESET COLOR
      doc.setTextColor(...BRAND_TEXT);
    };

    // Helper function to add Enhanced Footer (Company | Website | Phone | Page)
    const addFooter = (pageNum, totalPages) => {
      // Prevent duplicate rendering on same page
      const footerFlag = `_footer_rendered_${pageNum}`;
      if (doc[footerFlag]) return;
      doc[footerFlag] = true;

      const footHeight = 12;
      const footY = pageHeight - footHeight;

      // DRAW FOOTER BACKGROUND (Dark Blue #0B1F3A)
      doc.setFillColor(11, 31, 58);
      doc.rect(0, footY, pageWidth, footHeight, 'F');

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(255, 255, 255);

      const footTextY = footY + (footHeight / 2) + 1;

      // LEFT - Company Name
      doc.setFont('helvetica', 'bold');
      doc.text("SOLISPOWER GREENTECH PVT LTD", margin, footTextY);

      // CENTER - Website
      doc.setFont('helvetica', 'normal');
      doc.text("www.solispower.in", pageWidth / 2, footTextY, { align: "center" });

      // RIGHT - Contact & Page Info
      const footRightText = `7300061750  |  Page ${pageNum} of ${totalPages}`;
      doc.text(footRightText, pageWidth - margin, footTextY, { align: "right" });

      // RESET COLOR
      doc.setTextColor(...BRAND_TEXT);
    };

    // Helper function to check page break
    // Created on 2026-02-07 by Farhan Khan - Ensure quotation number header on new pages
    const checkPageBreak = (requiredHeight) => {
      const availableHeight = pageHeight - margin - footerHeight - yPosition;
      if (requiredHeight > availableHeight) {
        addFooter(totalPages, totalPages);
        drawPageBorder(totalPages);
        doc.addPage();
        totalPages++;
        drawPageBorder(totalPages);
        addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
        yPosition = 25; // Standardized spacing
        return true;
      }
      return false;
    };

    // ============================================
    // PAGE 1: COVER PAGE
    // ============================================
    const imageSectionHeight = pageHeight * 0.5;

    // Top half image (full width, center crop)
    if (coverImageBase64) {
      try {
        const imgProps = doc.getImageProperties(coverImageBase64);
        const imgWidth = imgProps.width || 1;
        const imgHeight = imgProps.height || 1;

        // COVER FIT (no stretch, full width)
        const scale = Math.max(
          pageWidth / imgWidth,
          imageSectionHeight / imgHeight
        );

        const finalWidth = imgWidth * scale;
        const finalHeight = imgHeight * scale;

        // Center crop positioning
        const imgX = (pageWidth - finalWidth) / 2;
        const imgY = (imageSectionHeight - finalHeight) / 2;

        // Add image (overflow will be hidden by the bottom rectangle)
        // We use PNG since the new URL is a PNG
        doc.addImage(coverImageBase64, 'PNG', imgX, imgY, finalWidth, finalHeight);
      } catch (error) {
        console.warn('Cover image placement failed:', error);
        doc.setFontSize(10);
        doc.text("Image not available", pageWidth / 2, imageSectionHeight / 2, { align: "center" });
      }
    }

    // Draw white rectangle to clip/hide any image overflow in the bottom half
    doc.setFillColor(255, 255, 255);
    doc.rect(0, imageSectionHeight, pageWidth, pageHeight - imageSectionHeight, 'F');

    // Bottom Section: Quotation DETAILS (ENHANCED)
    yPosition = imageSectionHeight + 15;

    // 1. Heading
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Proposal for Solar Power Plant", pageWidth / 2, yPosition, {
      align: "center",
    });

    yPosition += 12;

    // 2. 2-COLUMN GRID (6-6 LAYOUT)
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND_TEXT);

    const gridMargin = margin + 5;
    const col2X = pageWidth / 2 + 5;
    const rowSpacing = 9;
    const labelW = 38;

    // Prepare Date
    let formattedDate = "N/A";
    const rawDate = quotation?.quotation_date || quotation?.date || quotation?.created_at;
    if (rawDate) {
      const d = new Date(rawDate);
      formattedDate = `${String(d.getDate()).padStart(2, "0")} : ${String(d.getMonth() + 1).padStart(2, "0")} : ${d.getFullYear()}`;
    }

    // Prepare Address
    let addressParts = [billToAddress, billToCity, billToState].filter(part =>
      part && part.toLowerCase() !== "null" && part.toLowerCase() !== "n/a" && part.trim() !== ""
    );
    let fullCustomerAddress = addressParts.length > 0 ? addressParts.join(", ") : "";

    // Row 1: Quotation No. | Customer Name
    doc.setFont("helvetica", "bold"); doc.text("Quotation No.", gridMargin, yPosition);
    doc.text("Customer Name", col2X, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(`: ${quotationNo}`, gridMargin + labelW, yPosition);
    doc.text(`: ${leadName}`, col2X + labelW, yPosition);
    yPosition += rowSpacing;

    // Row 2: Quote Date | Site Name
    let siteNameStr = quotation?.leadData?.project_line_items?.site_name || quotation?.customer_name || '';
    doc.setFont("helvetica", "bold"); doc.text("Quote Date", gridMargin, yPosition);
    doc.text("Site Name", col2X, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(`: ${formattedDate}`, gridMargin + labelW, yPosition);
    doc.text(`: ${siteNameStr}`, col2X + labelW, yPosition);
    yPosition += rowSpacing;

    // Row 3: Site Address & Contact | Capacity + Created By
    doc.setFont("helvetica", "bold"); doc.text("Site Addr & Contact :", gridMargin, yPosition);
    doc.text("Capacity", col2X, yPosition);

    doc.setFont("helvetica", "normal");
    const addrContactLines = doc.splitTextToSize(`${fullCustomerAddress} | ${safePhone}`, (pageWidth / 2) - labelW - 10);
    doc.text(addrContactLines, gridMargin + labelW, yPosition);

    const capacityVal = safeLineItems[0]?.plant_capacity_required_by_client ? `${safeLineItems[0].plant_capacity_required_by_client} KW` : "";
    doc.text(`: ${capacityVal}`, col2X + labelW, yPosition);

    yPosition = yPosition + Math.max(addrContactLines.length * 5, rowSpacing) + 8;

    // 3. CONTACT DETAILS SECTION
    doc.setFont("helvetica", "bold");
    doc.text("Created By", gridMargin, yPosition);
    doc.text("Mobile No", gridMargin, yPosition + 6);
    doc.text("Email", gridMargin, yPosition + 12);

    doc.setFont("helvetica", "normal");
    doc.text(`: ${creatorName}`, gridMargin + 30, yPosition);
    doc.text(`: ${creatorMobile}`, gridMargin + 30, yPosition + 6);
    doc.text(`: ${creatorEmail}`, gridMargin + 30, yPosition + 12);
    yPosition += 18; // Reduced from 22

    const fullCustomerAddressNull = '' // Created for Spacing Issue so Better one Enter Space With This.
    // 4. ADDRESS CENTER ALIGN
    doc.setFont("helvetica", "normal");
    const centeredAddrLines = doc.splitTextToSize(fullCustomerAddressNull, pageWidth - 40);
    doc.text(centeredAddrLines, pageWidth / 2, yPosition, { align: "center" });
    yPosition += (centeredAddrLines.length * 5) + 6; // Reduced spacing after address

    // 5. COMPANY DETAILS (CENTER)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("SOLISPOWER GREENTECH PVT LTD", pageWidth / 2, yPosition, { align: "center" });
    yPosition += 5; // Reduced spacing
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND_TEXT);
    doc.text(`${COMPANY_ADDRESS_LINE1}, ${COMPANY_ADDRESS_LINE2}`, pageWidth / 2, yPosition, { align: "center" });
    yPosition += 5;
    doc.text("Contact:7300061750 | Email: info@solispower.in | Website: www.solispower.in", pageWidth / 2, yPosition, { align: "center" });
    yPosition += 6; // Small gap before icons

    // SAFETY CHECK (NO CUT OFF)
    if (yPosition > pageHeight - 25) {
      yPosition -= 12; // Pull up slightly if near footer
    }

    // 6. SOCIAL MEDIA ICONS WITH LINKS
    if (socialIcons) {
      const iconSize = 10; // Reduced from 12
      const iconSpacing = 8;
      const keys = ['linkedin', 'youtube', 'instagram', 'facebook'];
      const socialUrls = {
        linkedin: "https://in.linkedin.com/company/solis-power-greentech-pvt-ltd",
        youtube: "https://www.youtube.com/@solispowergreentech",
        instagram: "https://www.instagram.com/solispower.jpr",
        facebook: "https://www.facebook.com/share/1DbWng8Wk8/"
      };

      const validKeys = keys.filter(k => socialIcons[k]);
      const totalIconsWidth = (validKeys.length * iconSize) + ((validKeys.length - 1) * iconSpacing);
      let startX = (pageWidth - totalIconsWidth) / 2;

      // Ensure spacing if positioned near current bottom
      if (yPosition < pageHeight - 15) {
        yPosition += 2;
      }

      validKeys.forEach(key => {
        doc.addImage(socialIcons[key], "PNG", startX, yPosition, iconSize, iconSize);
        doc.link(startX, yPosition, iconSize, iconSize, { url: socialUrls[key] });
        startX += iconSize + iconSpacing;
      });
    }

    // ============================================
    // PAGE 2: QUOTATION SUMMARY
    // ============================================
    doc.addPage();
    totalPages++;
    drawPageBorder(totalPages);
    addHeader(totalPages, quotationNumber);
    addFooter(totalPages, totalPagesFinal);
    yPosition = 25; // Content immediately below header

    // QUOTATION Title (Enhanced: Bigger & Bold)
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    const quotationTitle = 'QUOTATION';
    doc.text(quotationTitle, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8; // Increased gap after title

    // Reset font for header details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // --- SECTION 1: CUSTOMER & PROJECT HEADER (Tabular Layout) ---
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);

    // Header Grid Config
    const headerCol1 = margin;
    const headerCol2 = pageWidth / 2 + 10;
    const headerRowHeight = 6;
    let headerY = yPosition;

    const quotationDate = quotation?.quotation_date || quotationData?.quotation_date || "";
    const formattedQuotationDate = quotationDate
      ? new Date(quotationDate).toLocaleDateString("en-GB")
      : "";

    // Labels
    doc.text("To:", headerCol1, headerY);
    doc.text(`Date: ${formattedQuotationDate}`, headerCol2, headerY);
    headerY += headerRowHeight;

    doc.setTextColor(...BRAND_TEXT);
    const firstItem = safeLineItems[0] || {};
    const capacityValue = firstItem.plant_capacity_required_by_client ? `${firstItem.plant_capacity_required_by_client} KW` : "";
    const phaseValue = firstItem.phase_qty || firstItem.phase || "";
    const capacityStr = (capacityValue && phaseValue) ? `${capacityValue} | ${phaseValue}` : (capacityValue || phaseValue || "N/A");

    siteNameStr = safe(quotation?.project_line_items?.site_name || "");

    // Safely Construct Address (Filtering empty, null, or N/A)
    addressParts = [billToAddress, billToCity, billToState].filter(part =>
      part && part.toLowerCase() !== "null" && part.toLowerCase() !== "n/a" && part.trim() !== ""
    );
    fullCustomerAddress = addressParts.length > 0 ? addressParts.join(", ") : "";

    // Dynamic Left Column (Customer Details)
    const customerFields = [
      { text: leadName, font: 'bold' },
      siteNameStr ? { text: siteNameStr, font: 'normal' } : null,
      fullCustomerAddress ? { text: fullCustomerAddress, font: 'normal' } : null,
      safePhone ? { text: `Contact No: ${safePhone}`, font: 'normal' } : null,
      safeEmail ? { text: `Email: ${safeEmail}`, font: 'normal' } : null
    ].filter(Boolean);

    // Fixed Right Column (Meta Details)
    const metaFields = [
      { text: "Quotation No.", font: 'bold' },
      { text: quotationNo, font: 'normal' },
      { text: "Created by", font: 'bold' },
      { text: createdBy, font: 'normal' },
      { text: "Capacity with Phase", font: 'bold' },
      { text: capacityStr, font: 'normal' }
    ];

    // Render Side-by-Side Dynamically with Row-Synchronous Auto-Wrap
    const maxRows = Math.max(customerFields.length, metaFields.length);

    for (let i = 0; i < maxRows; i++) {
      let maxLinesInRow = 1;

      if (customerFields[i]) {
        doc.setFont('helvetica', customerFields[i].font);
        const maxLeftWidth = headerCol2 - headerCol1 - 10;
        const leftLines = doc.splitTextToSize(customerFields[i].text, maxLeftWidth);
        maxLinesInRow = Math.max(maxLinesInRow, leftLines.length);
        doc.text(leftLines, headerCol1, headerY);
      }

      if (metaFields[i]) {
        doc.setFont('helvetica', metaFields[i].font);
        const maxRightWidth = pageWidth - margin - headerCol2;
        const rightLines = doc.splitTextToSize(metaFields[i].text, maxRightWidth);
        maxLinesInRow = Math.max(maxLinesInRow, rightLines.length);
        doc.text(rightLines, headerCol2, headerY);
      }

      headerY += (maxLinesInRow * headerRowHeight);
    }

    yPosition = headerY + 12;

    // --- SECTION 2: FINANCIAL LINE ITEMS TABLE (HIGH FIDELITY) ---
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Financial Line Items", margin, yPosition);
    yPosition += 6;

    // Prepare Financial Data
    const financeRows = [];

    // Add Individual Line Items from API
    safeLineItems.forEach(item => {
      const productName = item.product_name || "Solar Power Plant";
      const pCap = item.plant_capacity_required_by_client ? `${item.plant_capacity_required_by_client} KW` : "";
      const pPhase = item.phase_qty || item.phase || "";
      const plantCapacity = (pCap && pPhase) ? `${pCap} | ${pPhase}` : (pCap || pPhase || "-");
      const description = item.product_description || "";
      const rate = Number(item.unit_price || 0);
      const rowTotal = Number(item.net_amount || item.total_amount || item.gross_amount || 0);

      financeRows.push([
        productName,
        plantCapacity,
        description,
        formatCurrency(rate),
        formatCurrency(rowTotal)
      ]);
    });

    // Summary Section (API Values Only)
    const gstTotal = Number(quotation.gst_amount || quotation.tax_gst || 0);
    const subsidyAmt = Math.abs(Number(quotation.subsidy_amount || 0));
    const lineItemDiscountSum = safeLineItems.reduce((sum, item) => sum + Number(item.discount || 0), 0);
    const extraDiscount = Number(quotation.total_discount || quotation.discount || 0);
    const discountAmt = lineItemDiscountSum + extraDiscount;
    const effectivePrice = Number(quotation.effective_price || 0);
    const totalPayable = Number(quotation.payable_amount || quotation.total_payable_amount || 0);

    // Dynamic Summary Rows
    if (gstTotal !== 0) {
      financeRows.push(["GST", "", `${quotation.tax_percentage || "8.9%"}`, "", formatCurrency(gstTotal)]);
    }
    if (subsidyAmt !== 0) {
      financeRows.push(["SUBSIDY", "", "Net Deduction (Govt. Subsidy)", "", `- ${formatCurrency(subsidyAmt)}`]);
    }
    if (discountAmt !== 0) {
      financeRows.push(["DISCOUNT", "", "Extra Discount", "", `- ${formatCurrency(discountAmt)}`]);
    }

    financeRows.push([
      { content: "Effective Price", styles: { fontStyle: 'bold' } },
      "",
      "Price After Subsidy Credit",
      "",
      { content: formatCurrency(effectivePrice), styles: { fontStyle: 'bold' } }
    ]);

    financeRows.push([
      { content: "Total Amount Payable", styles: { fontStyle: 'bold' } },
      "",
      "Final Payable Amount",
      "",
      { content: formatCurrency(totalPayable), styles: { fontStyle: 'bold' } }
    ]);

    autoTable(doc, {
      startY: yPosition,
      margin: { left: margin, right: margin },
      head: [['Particulars', 'Capacity (KW)', 'Description', 'Rate / KW', 'Total amount']],
      body: financeRows,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 2,
        textColor: BRAND_TEXT,
        lineColor: [0, 0, 0], // Black borders
        lineWidth: 0.5,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [54, 71, 149],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        lineColor: [0, 0, 0],
        lineWidth: 0.5
      },
      bodyStyles: {
        fillColor: [131, 200, 242],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.5
      },
      columnStyles: {
        0: { cellWidth: 35, fontStyle: 'bold' },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 60 },
        3: { cellWidth: 37, halign: 'right', overflow: 'visible' },
        4: { cellWidth: 37, halign: 'right', fontStyle: 'bold', overflow: 'visible' }
      },
      didParseCell: function (data) {
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        if (pageNumber !== 2) return;

        if (data.section === 'body') {
          const rowText = data.row.raw?.[0] || "";
          const rowLabel = typeof rowText === 'object' ? (rowText.content || "") : rowText.toString();

          const highlightRows = ["GST", "SUBSIDY", "DISCOUNT", "Effective Price", "Total Amount Payable"];

          if (highlightRows.includes(rowLabel)) {
            data.cell.styles.fillColor = [82, 132, 193]; // #5284c1 - Blue shade
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    // --- SECTION 3: 2-COLUMN GRID (Notes/Bank | QR Code) ---
    const usableWidth = pageWidth - (margin * 2);
    const colWidth = usableWidth / 2;
    let gridY = yPosition;

    // LEFT COLUMN: Notes + Bank Details
    const leftColX = margin;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Notes:", leftColX, gridY);
    gridY += 6;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const notesText = "Please refer detailed Terms & Conditions and Bill of Material sections on the following pages for technical specifications.";
    const notesLines = doc.splitTextToSize(notesText, colWidth - 10);
    doc.text(notesLines, leftColX, gridY);
    gridY += (notesLines.length * 5) + 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Bank Details:", leftColX, gridY);
    gridY += 6;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const bankDetails = [
      `Bank Name: ${BANK_NAME}`,
      `Account Name: ${ACCOUNT_NAME}`,
      `Account Number: ${ACCOUNT_NO}`,
      `IFSC: ${IFSC}`,
      `Branch: ${BRANCH}`
    ];
    bankDetails.forEach(line => {
      doc.text(line, leftColX, gridY);
      gridY += 5;
    });

    // RIGHT COLUMN: QR Code (Aligned centered in its col)
    const rightColX = margin + colWidth;
    let qrY = yPosition;

    if (qrCodeBase64) {
      try {
        const qrSize = 45;
        const qrX = pageWidth - margin - qrSize - 6;
        doc.addImage(qrCodeBase64, 'PNG', qrX, qrY, qrSize, qrSize);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...BRAND_PRIMARY);
        doc.text("Scan and Pay", qrX + (qrSize / 2), qrY + qrSize + 5, { align: 'center' });
      } catch (qrErr) {
        console.warn("QR Code rendering failed:", qrErr);
      }
    }

    yPosition = Math.max(gridY, qrY + 60);

    // --- SECTION 4: SIGNATURES ---
    const sigLineY = yPosition + 10;
    const sigLineWidth = 50;

    const leftSignX = margin + 5;
    const rightSignX = pageWidth - margin - sigLineWidth - 5;

    // 1. Draw solid signature lines
    doc.setDrawColor(...BRAND_PRIMARY);
    doc.setLineWidth(0.5);
    doc.line(leftSignX, sigLineY, leftSignX + sigLineWidth, sigLineY);
    doc.line(rightSignX, sigLineY, rightSignX + sigLineWidth, sigLineY);

    // 2. Add signature labels BELOW lines
    const labelY = sigLineY + 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);

    doc.text("Customer Signature", leftSignX + (sigLineWidth / 2), labelY, { align: 'center' });
    doc.text("Sales Executive", rightSignX + (sigLineWidth / 2), labelY, { align: 'center' });

    yPosition = labelY + 15;

    // ============================================
    // PAGE 3: BILL OF MATERIAL & MOUNTING STRUCTURE
    // ============================================
    doc.addPage();
    totalPages++;
    drawPageBorder(totalPages);
    addHeader(totalPages, quotation.estimate_number || 'QUOTATION');

    console.log("QUOTATION DATA FOR PDF", quotation);

    function groupBySection(items) {
      if (!items || !items.length) return [];
      const map = {};

      items.forEach(item => {
        const section = item.section || item.section_name || item.display_category || "General";

        if (!map[section]) {
          map[section] = {
            name: section,
            rows: []
          };
        }

        map[section].rows.push(item);
      });

      return Object.values(map);
    }

    const bomSections = groupBySection(bomItems || quotation.bomItems || []);

    const normalizeSections = (sections) => {
      return (sections || []).map(sec => ({
        ...sec,
        section_name: sec.section_name || sec.roof_type || "Structure Section",
        roof_type: (sec.roof_type || sec.section_name || "").toUpperCase(),
        parts: sec.parts || sec.rows || sec.items || sec.components || []
      }));
    };

    const mountingSections = normalizeSections(safeMountingStructures || quotation.mounting_structure_sections || quotation.mounting_structure);

    console.log("BOM ITEMS", bomItems);
    console.log("GROUPED BOM", bomSections);
    console.log("MOUNTING", quotation.mountingStructures);
    console.log("NORMALIZED MOUNTING", mountingSections);

    // Make mapping setup
    const masters = quotationData.masters || {};
    const makeMap = (masters.makeList || []).reduce((acc, item) => {
      acc[item.id] = item.name;
      return acc;
    }, {});

    // Helper to format values that might be arrays OR postgres strings
    const formatBOMValue = (val) => {
      if (!val) return null;
      if (Array.isArray(val)) return val.join(", ");
      if (typeof val === "string" && val.includes("{")) {
        return val.replace(/[{}"]/g, "").split(",").map(s => s.trim()).filter(Boolean).join(", ");
      }
      return val;
    };

    // --- HELPER: RENDER BOM TABLE (8 COLUMNS) ---
    const renderBOMSectionTable = (section) => {
      const rows = section.rows || [];
      if (!rows.length) return;

      // Section Title
      doc.setFontSize(12);
      doc.setTextColor(...BRAND_TEXT);
      doc.text(section.name || "Section", leftMarginVal, yPosition);
      yPosition += 6;

      // REQUIRED TABLE HEADERS (IN ORDER)
      const headers = ["Item Name", "Specs", "Category", "Size/Range", "Make", "Unit", "Qty", "Warranty"];

      const mapRow = (item) => {
        // Requirement: Use lineItem.specification (snapshot labels) as primary source
        let specsValue = item.specification || "-";

        return [
          item.item_name || "-",
          specsValue,
          formatBOMValue(item.category) || item.display_category || "-",
          item.size_range_capacity || "-",
          formatBOMValue(item.make) || "-",
          item.unit || "-",
          item.quantity || "-",
          item.manufacturer_warranty || "-"
        ];
      };

      const tableData = rows.map(item => mapRow(item));
      if (!tableData.length) tableData.push(["-", "-", "-", "-", "-", "-", "-", "-"]);

      autoTable(doc, {
        startY: yPosition,
        head: [headers],
        body: tableData,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak", textColor: BRAND_TEXT },
        headStyles: { fillColor: [54, 71, 149], textColor: [255, 255, 255], fontStyle: "bold" },
        bodyStyles: { fillColor: [131, 200, 242], textColor: [0, 0, 0] },
        margin: { left: leftMarginVal, right: leftMarginVal }, // Full width alignment
        columnStyles: {
          0: { cellWidth: 35 }, // Item Name
          1: { cellWidth: 35 }, // Specs
          2: { cellWidth: 20 }, // Category
          3: { cellWidth: 20 }, // Size/Range
          4: { cellWidth: 20 }, // Make
          5: { cellWidth: 10 }, // Unit
          6: { cellWidth: 10 }, // Qty
          7: { cellWidth: 32 }  // Warranty (expanded using the extra space on right side)
        }
      });

      yPosition = doc.lastAutoTable.finalY + 10;
      if (yPosition > 260) {
        doc.addPage();
        totalPages++;
        drawPageBorder(totalPages);
        addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
        yPosition = 25; // Standardized spacing
      }
    };

    // --- HELPER: RENDER MOUNTING STRUCTURE SECTION (MATCHES UI) ---
    const renderMountingSection = (section) => {
      const parts = section.parts || [];
      const roofType = (section.roof_type || "").toUpperCase();
      const isRCCorGround = roofType.includes("RCC") || roofType.includes("GROUND");
      const isTinShed = roofType.includes("TIN SHED");
      const isCombo = roofType.includes("COMBO");
      const isSpecial = roofType.includes("SPECIAL STRUCTURE");

      // 1. SECTION TITLE (Bold & Larger)
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND_PRIMARY);
      const sectionTitle = section.section_name || roofType || "Mounting Structure";
      doc.text(sectionTitle, leftMarginVal, yPosition);
      yPosition += 6;

      // 2. DETAILS SECTION (2-COL GRID - Key-Value Pairs)
      const details = [];
      const isActuallySpecial = isSpecial || (section.section_name || "").toUpperCase() === "SPECIAL STRUCTURE";

      if (isRCCorGround && !isActuallySpecial) {
        details.push({ label: "FRONT HEIGHT (FT):", value: section.structure_height_front ?? section.height ?? "N/A" });
        details.push({ label: "BACK HEIGHT (FT):", value: section.structure_height_back ?? "N/A" });
      } else if (isTinShed && !isActuallySpecial) {
        details.push({ label: "STRUCTURE TYPE:", value: formatBOMValue(section.tin_shed_structure_type || section.structure_type) || "N/A" });
        details.push({ label: "MOUNTING TYPE:", value: formatBOMValue(section.tin_shed_mounting_type || section.mounting_type) || "N/A" });
      } else if (isCombo && !isActuallySpecial) {
        details.push({ label: "PANELS RCC:", value: section.combo_panels_rcc ?? "-" });
        details.push({ label: "PANELS GROUND:", value: section.combo_panels_ground ?? "-" });
        details.push({ label: "PANELS TIN SHED:", value: section.combo_panels_tinshed ?? "-" });
      } else if (isActuallySpecial) {
        details.push({ label: "SPECIAL STRUCTURE TYPE:", value: section.special_structure_type ?? "-" });
        details.push({ label: "LENGTH (FT):", value: section.special_structure_length ?? "-" });
        if (section.special_structure_specs) {
          details.push({ label: "SPECS:", value: section.special_structure_specs });
        }
        if (section.special_structure_type === "OTHERS" && section.others_text) {
          details.push({ label: "OTHERS:", value: section.others_text });
        }
      }

      // Add any additional fields mentioned in UI or prompt requirements
      const extraFields = [
        { key: "make", label: "MAKE:" },
        { key: "wind_speed", label: "WIND SPEED:" },
        { key: "material", label: "MATERIAL:" },
        { key: "coating", label: "COATING:" },
        { key: "description", label: "DESCRIPTION:" }
      ];

      extraFields.forEach(f => {
        if (section[f.key]) {
          details.push({ label: f.label, value: formatBOMValue(section[f.key]) });
        }
      });

      if (details.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(...BRAND_TEXT);

        const labelWidth = 35;
        const col2Offset = leftMarginVal + (contentWidth / 2);
        const valWidth = (contentWidth / 2) - labelWidth - 2;

        for (let i = 0; i < details.length; i += 2) {
          const left = details[i];
          const right = details[i + 1];

          doc.setFont("helvetica", "bold");
          doc.text(left.label, leftMarginVal, yPosition);
          doc.setFont("helvetica", "normal");
          const leftVal = doc.splitTextToSize(String(left.value), valWidth);
          doc.text(leftVal, leftMarginVal + labelWidth, yPosition);

          let rowHeight = leftVal.length * 5;

          if (right) {
            doc.setFont("helvetica", "bold");
            doc.text(right.label, col2Offset, yPosition);
            doc.setFont("helvetica", "normal");
            const rightVal = doc.splitTextToSize(String(right.value), valWidth);
            doc.text(rightVal, col2Offset + labelWidth, yPosition);
            rowHeight = Math.max(rowHeight, rightVal.length * 5);
          }

          yPosition += rowHeight;
          if (yPosition > 270) {
            doc.addPage(); totalPages++; drawPageBorder(totalPages); addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
            yPosition = 25;
          }
        }
        yPosition += 4;
      }

      // 3. PARTS SECTION (Tables)
      if (parts.length > 0) {
        let headers = [];
        let body = [];

        if (isCombo) {
          headers = ["S No.", "Roof Type", "No. of Panels"];
          body = parts.map((p, idx) => [p.s_no || idx + 1, p.roof_type || p.item_name || "-", p.no_of_panels || "-"]);
        } else if (isSpecial) {
          headers = ["Name", "Type", "Specification", "Length (FT)"];
          body = parts.map(p => {
            if (p.input_type === "TEXT_BOX") {
              return [{ content: `${p.item_name}: ${p.specification || "-"}`, colSpan: 4, styles: { fontStyle: 'bold' } }];
            }
            return [
              p.item_name || "-",
              p.special_structure_type || "-",
              p.specification || p.special_structure_specs || "-",
              p.special_structure_length || "-"
            ];
          });
        } else {
          // RCC / GROUND / TIN SHED TABLE
          headers = ["Name", "Specs", "Size", "Shape", "Make"];
          body = parts.map(p => {
            if (p.item_name === "OTHERS") {
              return [{ content: `OTHERS: ${p.specification || "-"}`, colSpan: 5, styles: { fontStyle: 'italic', fillColor: [245, 245, 245] } }];
            }
            return [
              p.item_name || "-",
              p.specification || "-",
              p.size || "-",
              p.shape || "-",
              formatBOMValue(p.make) || "-"
            ];
          });
        }

        autoTable(doc, {
          startY: yPosition,
          head: [headers],
          body: body,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 3, textColor: BRAND_TEXT },
          headStyles: { fillColor: [54, 71, 149], textColor: [255, 255, 255], fontStyle: 'bold' },
          bodyStyles: { fillColor: [131, 200, 242], textColor: [0, 0, 0] },
          margin: { left: leftMarginVal, right: leftMarginVal }, // Full width alignment
          didDrawCell: (data) => {
            if (data.row.index === 0 && data.section === 'body') {
              // Highlight row logic if needed
            }
          }
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      // 4. NOTE SECTION (If exists in section)
      const noteText = section.note || section.notes;
      if (noteText) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(100, 100, 100);
        const noteLines = doc.splitTextToSize(`NOTE: ${noteText}`, contentWidth);
        doc.text(noteLines, leftMarginVal, yPosition);
        yPosition += (noteLines.length * 5) + 8;
      }

      // 5. CLEANING SYSTEM SECTION (If exists in section)
      if (section.cleaning_system) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...BRAND_PRIMARY);
        doc.text("Cleaning System:", leftMarginVal, yPosition);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...BRAND_TEXT);
        const cleaningLines = doc.splitTextToSize(String(section.cleaning_system), contentWidth - 30);
        doc.text(cleaningLines, leftMarginVal + 30, yPosition);
        yPosition += (cleaningLines.length * 5) + 8;
      }

      // PAGE BREAK CHECK
      if (yPosition > 260) {
        doc.addPage();
        totalPages++;
        drawPageBorder(totalPages);
        addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
        yPosition = 25; // Standardized spacing
      }
    };

    // --- 1. BILL OF MATERIAL DETAILS ---
    doc.setFontSize(14);
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Bill of Material Details", leftMarginVal, 25);
    yPosition = 35;

    if (bomSections && Array.isArray(bomSections) && bomSections.length > 0) {
      bomSections.forEach(renderBOMSectionTable);
    } else {
      doc.setFontSize(12);
      doc.setTextColor(...BRAND_TEXT);
      doc.text("No Bill of Material available", leftMarginVal, yPosition);
      yPosition += 10;
    }

    // --- 2. MOUNTING STRUCTURE DETAILS (RENDER IMMEDIATELY AFTER BOM) ---
    console.log("Mounting Structure Data for PDF:", safeMountingStructures);
    if (mountingSections && Array.isArray(mountingSections) && mountingSections.length > 0) {
      // Check if we need a new page or just some spacing
      if (yPosition > 220) {
        doc.addPage();
        totalPages++;
        drawPageBorder(totalPages);
        addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
        yPosition = 25; // Standardized spacing
      } else {
        yPosition += 10;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND_PRIMARY);
      doc.text("Mounting Structure Details", leftMarginVal, yPosition);
      yPosition += 10;

      mountingSections.forEach(renderMountingSection);
    } else {
      console.log("Mounting Structure is empty or undefined - skipping section rendering");
    }

    console.log("BOM COUNT", bomSections.length);
    console.log("MOUNTING COUNT", mountingSections.length);

    // ============================================
    // PAGE: TERMS & CONDITIONS (Smart Flow)
    // ============================================
    console.log("Before Terms Page. Current yPosition:", yPosition);
    console.log("Current Total Pages:", totalPages);

    if (yPosition > 200) {
      doc.addPage();
      totalPages++;
      drawPageBorder(totalPages);
      addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
      yPosition = 25;
    } else {
      yPosition += 10;
    }


    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    const termsTitle = 'TERMS & CONDITIONS';
    const termsTitleWidth = doc.getTextWidth(termsTitle);
    doc.text(termsTitle, centerX - termsTitleWidth / 2, yPosition);
    yPosition += 6;

    const renderTermsSection = (title, contentLines) => {
      // Check page break for title
      if (yPosition > 265) {
        doc.addPage();
        totalPages++;
        drawPageBorder(totalPages);
        addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
        yPosition = 25; // Standardized spacing
      }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BRAND_PRIMARY);
      doc.text(title, leftMarginVal, yPosition);
      yPosition += 5;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...BRAND_TEXT);
      contentLines.forEach(item => {
        if (!item) {
          yPosition += 2;
          return;
        }
        const lines = doc.splitTextToSize(item, contentWidth - 5);
        if (yPosition + (lines.length * 4) > 275) {
          doc.addPage();
          totalPages++;
          drawPageBorder(totalPages);
          addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
          yPosition = 25;
        }
        doc.text(lines, leftMarginVal + 5, yPosition);
        yPosition += lines.length * 4 + 2;
      });
      yPosition += 4;
    };

    renderTermsSection('SOLIS SCOPE', [
      '1. All applicable taxes are on turnkey project basis with Installation and commissioning of plant.',
      '2. Transport and Octroi, if any, is in Solis Scope.',
      '3. All statutory compliances and Govt. fee related to solar power plant (Application, CEIG, Testing of Meters and liaising with DISCOM).',
      '4. Payment of expenses towards approval and fee related to solar power plant only.',
      '5. Basic training for solar plant operation to person nominated by customer.'
    ]);

    renderTermsSection('CUSTOMER SCOPE', [
      '1. For optimal performance, clean the solar modules in every 15 days (suggested).',
      '2. Nominate one person for training on basic solar plant operation.',
      '3. If any electrical modification or equipment, apart from solar power plant, are required by DISCOM (e.g., ELCB, changeover switch, etc.), the customer shall bear the same.',
      '4. To ensure site clearance, ladders, water, and electricity for smooth installation and commissioning.',
      '5. All statutory compliance and government fees apart from solar power plant (New Connection, Load extension, Phase change, Name correction, Name transfer, Extra meter and liaising with DISCOM).',
      '6. To provide uninterrupted internet connectivity for regular plant monitoring.',
      '7. To provide safe roof access for Solis Team.',
      '8. To provide all required documents for DISCOM and other statutory approvals.',
      '9. To arrange ladders and stools for Solis Team during execution and after sales service.',
      '10. Any core cutting or wall cutting (Zhiri) shall be in the customer’s scope. No concealed wiring shall be done by Solis Team. Customer should arrange concealed wiring with their own resources.',
      '11. To provide a safe place for material unloading and storage during execution.',
      '12. DISCOM related issues after commissioning like billing issues, unit adjustment issues, etc. is not in Solis Scope. Customer has to do it by their own efforts.',
      '13. Harmonics of the existing electrical system should be okay and in desired range.',
      '14. Any additional taxes imposed after signing up of Quotation, such as (GST/Custom Duty/Safe Guard/Local/Others) by Govt. or Competent Authority shall be borne by customer.'
    ]);

    renderTermsSection('GENERAL TERMS & CONDITIONS', [
      '1. Material dispatch and installation shall commence only after DISCOM approval.',
      '2. This quotation is valid for 7 days only.',
      '3. Solis will provide materials mentioned in the bill of material list. Solis is not liable to provide or install any other materials which are not mentioned in bill of material or in notes section. (e.g. meter boxes, extra meters, stairs, walkways, cable trays, additional support, extra civil work, drainage system, etc.).',
      '4. Solis team will not do any other work in customer premises which is not related to solar power plant installation.',
      '5. Solis team will complete erection work generally in 3 visits. For more visits required because of site related issues, an additional charge will be applicable unless until requested by the customer prior to signing of quotation and agreed by Solis in writing.'
    ]);

    renderTermsSection('SUBSIDY CLAUSES', [
      '1. Subsidy amount, if applicable, shall be directly credited to the customer’s account. Solis will handle all Government portal related work on your behalf.',
      '2. Subsidy disbursement timelines are governed by the Government policies and officials.',
      '3. Solis shall not be liable or responsible for any delays or non-receipt of subsidy amounts.'
    ]);

    renderTermsSection('SERVICE & WARRANTY CLAUSE', [
      '1. If any product is found damaged or burnt during service visit and not covered under manufacturer’s warranty terms & conditions, then repair & replacement will be provided at an additional charge.',
      '2. All equipment including Solar panels, inverter, ACDB, DCDB, Meters, wires etc. carry manufacturer warranty for a specific period. All warranties will be as per the respective manufacturer’s warranty terms & conditions. Solis will assist in warranty claims, if required, but shall not be responsible for providing warranty.',
      '3. Warranty does not cover damages due to external hazardous conditions.'
    ]);

    renderTermsSection('WARRANTY EXCLUSIONS: Warranty does not cover failures due to:', [
      '1. Accident, misuse, negligence or lack of maintenance.',
      '2. Normal wear and tear or damages caused intentionally or forcefully.',
      '3. Natural calamities such as fire, earthquake, flood, hailstorm, natural disasters etc.',
      '4. Alterations, modifications or repairs by unauthorized person.',
      '5. Replaced components carry warranty only for the remaining original warranty period.',
      '6. Customer is liable to return the defective or replaced material to the vendor.'
    ]);

    renderTermsSection('Force Majeure', [
      '1. Acts of God (like fire, flood, or natural catastrophes).',
      '2. Acts of war, terrorism, riot or civil unrest.',
      '3. Government actions or embargoes.',
      '4. Strikes or labour disturbances.',
      '5. Utility or service failures not due to the Company’s negligence.'
    ]);

    renderTermsSection('OPERATION & MAINTENANCE (O&M)', [
      '1. Five years of O&M support (Applicable only for PMSGMBY)',
      '2. Customer must inform any fault within 24 hours.',
      '3. Service visit shall be done within 48 working hours.'
    ]);

    renderTermsSection('SCHEDULE FOR SITE COMPLETION', [
      '1. Material dispatch within 6 weeks from order confirmation and payment.',
      '2. Complete installation and commissioning within 60–70 days (approx.) after:\n   a. Project accreditation,\n   b. Contract agreement,\n   c. Site possession.'
    ]);

    const paymentTermsContent = quotation.payment_terms ? htmlToLines(quotation.payment_terms) : [];
    renderTermsSection('PAYMENT TERMS', [
      ...paymentTermsContent,
      '',
      'Any delay in payment on customer part may cause delay in work completion and can be penalized for demerge.',
      '',
      'Non-payment or short-payment of agreed amount may result in not providing after sales service or O&M mentioned in quotation. Non payment may also lead to voiding all the product warranties and warranty support from Solis.'
    ]);

    // Add borders, quotation number headers, and footers to all pages
    // Add one final blank page (reserved for future use)
    doc.addPage();

    // Add borders, quotation number headers, and footers to all pages
    const totalPagesFinalActual = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPagesFinalActual; i++) {
      doc.setPage(i);

      // RESET FLAGS for the final high-fidelity pass
      doc[`_header_rendered_${i}`] = false;
      doc[`_footer_rendered_${i}`] = false;

      // Apply Page Border (Page 2+ Sidebars)
      drawPageBorder(i);

      // Apply Global Header (Skip Page 1)
      addHeader(i, quotationNumber);

      // Apply Global Footer (All Pages)
      addFooter(i, totalPagesFinalActual);
    }

    // Save PDF
    const fileName = `Quotation_${quotation.estimate_number || 'Draft'}.pdf`;
    doc.save(fileName);

    console.log('PDF Generation - Success:', fileName);
  } catch (error) {
    console.error('PDF Generation - Critical Error:', error);
    throw error;
  }
};